import jsPDF from "jspdf";
import { TEACHER_RESOURCES, type ProjectTopic } from "@/data/endOfYearProject";

const MARGIN = 56; // 0.78"
const PAGE_W = 612; // Letter width in pt
const PAGE_H = 792;
const CONTENT_W = PAGE_W - MARGIN * 2;
const PRIMARY: [number, number, number] = [37, 99, 235]; // ~hsl(210)
const MUTED: [number, number, number] = [100, 116, 139];
const TEXT: [number, number, number] = [15, 23, 42];

export function downloadTopicResourcePdf(topic: ProjectTopic): boolean {
  const res = TEACHER_RESOURCES[topic.id];
  if (!res) return false;

  const doc = new jsPDF({ unit: "pt", format: "letter" });
  let y = MARGIN;

  function ensureSpace(needed: number) {
    if (y + needed > PAGE_H - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
  }

  function setColor(rgb: [number, number, number]) {
    doc.setTextColor(rgb[0], rgb[1], rgb[2]);
  }

  function writeWrapped(text: string, lineHeight = 14, indent = 0) {
    const lines = doc.splitTextToSize(text, CONTENT_W - indent);
    for (const line of lines) {
      ensureSpace(lineHeight);
      doc.text(line, MARGIN + indent, y);
      y += lineHeight;
    }
  }

  function sectionHeading(label: string) {
    ensureSpace(34);
    y += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    setColor(PRIMARY);
    const upper = label.toUpperCase();
    doc.text(upper, MARGIN, y);
    const underlineWidth = doc.getTextWidth(upper);
    y += 4;
    doc.setDrawColor(PRIMARY[0], PRIMARY[1], PRIMARY[2]);
    doc.setLineWidth(0.8);
    doc.line(MARGIN, y, MARGIN + underlineWidth, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    setColor(TEXT);
  }

  // Header band
  doc.setFillColor(PRIMARY[0], PRIMARY[1], PRIMARY[2]);
  doc.rect(0, 0, PAGE_W, 6, "F");

  // Eyebrow
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  setColor(MUTED);
  doc.text("ELEMENTPAD - END-OF-YEAR PROJECT", MARGIN, y);
  y += 26;

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  setColor(TEXT);
  const number = String(topic.number).padStart(2, "0");
  writeWrapped(`Topic ${number}. ${topic.title}`, 24);

  // TEKS badge
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  setColor(MUTED);
  doc.text(`TEKS ${topic.teks}`, MARGIN, y);
  y += 22;

  // Objective
  doc.setFont("helvetica", "italic");
  doc.setFontSize(11);
  setColor(TEXT);
  writeWrapped(topic.objective, 15);

  // Lesson starter
  sectionHeading("Lesson starter");
  doc.setFont("helvetica", "italic");
  writeWrapped(`"${res.lessonStarter}"`, 15);
  doc.setFont("helvetica", "normal");

  // Instructions
  sectionHeading("Instructions");
  res.instructions.forEach((step, i) => {
    const prefix = `${i + 1}.`;
    ensureSpace(16);
    doc.setFont("helvetica", "bold");
    doc.text(prefix, MARGIN, y);
    doc.setFont("helvetica", "normal");
    writeWrapped(step, 14, 22);
    y += 2;
  });

  // Pathways
  sectionHeading("Pathways");
  topic.pathways.forEach((p) => {
    ensureSpace(20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    setColor(PRIMARY);
    doc.text(`[${p.level}] ${p.title}`, MARGIN, y);
    y += 14;
    setColor(TEXT);
    doc.setFont("helvetica", "normal");
    writeWrapped(p.description, 14);
    y += 4;
  });

  // Suggested tools
  sectionHeading("Suggested tools");
  res.toolNotes.forEach((n) => {
    ensureSpace(14);
    doc.text("\u2022", MARGIN, y);
    writeWrapped(n, 14, 14);
  });

  // Links
  sectionHeading("Tool links");
  const origin =
    typeof window !== "undefined" ? window.location.origin : "https://elementpad.net";
  topic.tools.forEach((t) => {
    ensureSpace(14);
    doc.setFont("helvetica", "normal");
    setColor(TEXT);
    doc.text("\u2022 ", MARGIN, y);
    doc.text(`${t.label}:`, MARGIN + 12, y);
    setColor(PRIMARY);
    const url = `${origin}${t.path}`;
    doc.textWithLink(url, MARGIN + 12 + doc.getTextWidth(`${t.label}: `), y, { url });
    y += 14;
  });

  // Footer on every page
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    setColor(MUTED);
    doc.text(
      `Generated ${new Date().toLocaleString()}  -  elementpad.net`,
      MARGIN,
      PAGE_H - 24,
    );
    doc.text(`Page ${i} of ${pages}`, PAGE_W - MARGIN, PAGE_H - 24, { align: "right" });
  }

  doc.save(`elementpad-topic-${number}-${topic.id}.pdf`);
  return true;
}
